import type ExcelJS from "exceljs";

import { INLINE_MD_PATTERN, SAFE_URL_REGEX } from "@/utils/inlineMarkdown";

interface Segment {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  link?: string;
}

const parseSegments = (text: string): Segment[] => {
  const regex = new RegExp(INLINE_MD_PATTERN, "g");
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) });
    }

    if (match[2] !== undefined) {
      parseSegments(match[2]).forEach((s) => segments.push({ ...s, bold: true }));
    } else if (match[4] !== undefined) {
      parseSegments(match[4]).forEach((s) => segments.push({ ...s, bold: true }));
    } else if (match[6] !== undefined && match[7] !== undefined) {
      const linkText = match[6];
      const linkUrl = match[7];

      if (SAFE_URL_REGEX.test(linkUrl)) {
        parseSegments(linkText).forEach((s) => segments.push({ ...s, link: linkUrl }));
      } else {
        segments.push({ text: linkText });
      }
    } else if (match[9] !== undefined) {
      parseSegments(match[9]).forEach((s) => segments.push({ ...s, italic: true }));
    } else if (match[11] !== undefined) {
      parseSegments(match[11]).forEach((s) => segments.push({ ...s, italic: true }));
    } else if (match[13] !== undefined) {
      segments.push({ text: match[13], code: true });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) });
  }

  return segments;
};

const HYPERLINK_COLOUR = "FF0563C1";

const segmentFont = (
  segment: Segment,
  baseFont?: Partial<ExcelJS.Font>,
  codeFontFamily?: string,
): Partial<ExcelJS.Font> => {
  const font: Partial<ExcelJS.Font> = { ...(baseFont ?? {}) };

  if (segment.bold) font.bold = true;
  if (segment.italic) font.italic = true;
  if (segment.code) font.name = codeFontFamily ?? "Consolas";
  if (segment.link) {
    font.color = { argb: HYPERLINK_COLOUR };
    font.underline = true;
  }

  return font;
};

export interface ExcelMarkdownCell {
  value: ExcelJS.CellValue;
  font?: Partial<ExcelJS.Font>;
}

/**
 * Convert a string containing inline markdown (bold, italic, inline code,
 * links) into an ExcelJS cell value plus optional font override.
 *
 * - If the cell has no markdown markers, returns the original text.
 * - If the cell is exactly one link (optionally wrapped in bold/italic),
 *   returns a hyperlink cell value with merged formatting on the font.
 * - Otherwise returns rich text segments. Links inside rich text are styled
 *   blue + underline but are not individually clickable (ExcelJS limitation
 *   — only one hyperlink per cell).
 */
export const markdownToExcelCell = (
  text: string | number | boolean | null | undefined,
  baseFont?: Partial<ExcelJS.Font>,
  codeFontFamily?: string,
): ExcelMarkdownCell => {
  if (text === null || text === undefined) {
    return { value: text ?? "", font: baseFont };
  }

  if (typeof text !== "string") {
    return { value: text, font: baseFont };
  }

  const segments = parseSegments(text);

  if (segments.length === 0) {
    return { value: text, font: baseFont };
  }

  const hasFormatting = segments.some((s) => s.bold || s.italic || s.code || s.link);

  if (!hasFormatting) {
    // No surviving formatting (e.g. all links had unsafe URLs). Concatenate the
    // parsed plain-text segments so the rendered output strips the markdown
    // markers, matching `renderInlineMarkdown`/`stripInlineMarkdown` behaviour.
    const joined = segments.map((s) => s.text).join("");

    return { value: joined === "" ? text : joined, font: baseFont };
  }

  const linkSegments = segments.filter((s) => s.link);
  const allSameLink =
    linkSegments.length > 0 &&
    linkSegments.every((s) => s.link === linkSegments[0].link) &&
    segments.every((s) => s.link === linkSegments[0].link);

  if (allSameLink) {
    const url = linkSegments[0].link as string;
    const linkText = segments.map((s) => s.text).join("");
    const font: Partial<ExcelJS.Font> = {
      ...(baseFont ?? {}),
      color: { argb: HYPERLINK_COLOUR },
      underline: true,
    };

    if (segments.some((s) => s.bold)) font.bold = true;
    if (segments.some((s) => s.italic)) font.italic = true;

    return {
      value: { text: linkText, hyperlink: url } as ExcelJS.CellHyperlinkValue,
      font,
    };
  }

  const richText: ExcelJS.RichText[] = segments
    .filter((s) => s.text.length > 0)
    .map((segment) => ({
      text: segment.text,
      font: segmentFont(segment, baseFont, codeFontFamily),
    }));

  if (richText.length === 0) {
    return { value: text, font: baseFont };
  }

  return {
    value: { richText } as ExcelJS.CellRichTextValue,
    font: baseFont,
  };
};

/**
 * Apply markdown-aware value + font to an ExcelJS cell.
 */
export const setMarkdownCell = (
  cell: ExcelJS.Cell,
  text: string | number | boolean | null | undefined,
  baseFont?: Partial<ExcelJS.Font>,
  codeFontFamily?: string,
): void => {
  const result = markdownToExcelCell(text, baseFont, codeFontFamily);

  cell.value = result.value;
  if (result.font && Object.keys(result.font).length > 0) {
    cell.font = result.font;
  }
};
